import React from 'react'
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
} from '@mui/material'
import {
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material'

const IssueDashboard = ({ issues }) => {
  console.log("Issues:", issues);
  // Calculate basic statistics
  const totalIssues = issues.length
  const openIssues = issues.filter((issue) => issue.status === 'Open').length
  const inProgressIssues = issues.filter(
    (issue) => issue.status === 'In Progress'
  ).length
  const resolvedIssues = issues.filter(
    (issue) => issue.status === 'Resolved'
  ).length
  const closedIssues = issues.filter(
    (issue) => issue.status === 'Closed'
  ).length

  const highPriorityIssues = issues.filter(
    (issue) =>  
      issue.priority === 'High' &&
      ['Open', 'In Progress', 'Under Review', 'Material Awaited', 'OEM', 'Resolved'].includes(issue.status)
  ).length

  const overdueIssues = issues.filter((issue) => {
    if (!issue.expectedClosingDate) return false
    const now = new Date()
    const expectedDate = new Date(issue.expectedClosingDate)
    return expectedDate < now && ['Open', 'Under Review', 'In Progress', 'Material Awaited', 'OEM', 'Resolved'].includes(issue.status)
  }).length

  // Calculate completion rate
  const completionRate =
    totalIssues > 0 ? ((resolvedIssues + closedIssues) / totalIssues) * 100 : 0

  // Calculate average resolution time
  const calculateAvgResolutionTime = () => {
    const resolvedWithTime = issues.filter(
      (issue) =>
        ['Resolved', 'Closed'].includes(issue.status) &&
        issue.createdAt &&
        issue.updatedAt
    )

    if (resolvedWithTime.length === 0) return 'N/A'

    const totalDays = resolvedWithTime.reduce((sum, issue) => {
      const created = new Date(issue.createdAt)
      const resolved = new Date(issue.resolvedAt || issue.updatedAt)
      const days = Math.ceil((resolved - created) / (1000 * 60 * 60 * 24))
      return sum + days
    }, 0)

    return `${(totalDays / resolvedWithTime.length).toFixed(1)} days`
  }

  // Category breakdown
  const categoryBreakdown = issues.reduce((acc, issue) => {
    acc[issue.category] = (acc[issue.category] || 0) + 1
    return acc
  }, {})

  // Plant breakdown
  const plantBreakdown = issues.reduce((acc, issue) => {
    const plantName = issue.plantName || 'Unknown Plant'
    acc[plantName] = (acc[plantName] || 0) + 1
    return acc
  }, {})

  // Recent trend (last 7 days vs previous 7 days)
  const now = new Date()
  const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
  const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000)

  const recentIssues = issues.filter(
    (issue) => new Date(issue.createdAt) >= lastWeek
  ).length
  const previousWeekIssues = issues.filter((issue) => {
    const created = new Date(issue.createdAt)
    return created >= twoWeeksAgo && created < lastWeek
  }).length

  const trendPercentage =
    previousWeekIssues > 0
      ? (
          ((recentIssues - previousWeekIssues) / previousWeekIssues) *
          100
        ).toFixed(1)
      : recentIssues > 0
      ? '+100'
      : '0'

  const avgResolutionTime = calculateAvgResolutionTime()

  const dashboardCards = [
    {
      title: 'Total Issues',
      value: totalIssues,
      icon: <AssignmentIcon />,
      color: '#1976d2',
      subtitle: 'All time',
    },
    {
      title: 'Open Issues',
      value: openIssues,
      icon: <WarningIcon />,
      color: '#d32f2f',
      subtitle: 'Needs attention',
    },
    {
      title: 'In Progress',
      value: inProgressIssues,
      icon: <ScheduleIcon />,
      color: '#ed6c02',
      subtitle: 'Being worked on',
    },
    {
      title: 'Resolved',
      value: resolvedIssues,
      icon: <CheckCircleIcon />,
      color: '#2e7d32',
      subtitle: 'Recently completed',
    },
    {
      title: 'High Priority',
      value: highPriorityIssues,
      icon: <WarningIcon />,
      color: '#d32f2f',
      subtitle: 'Urgent attention needed',
    },
    {
      title: 'Overdue',
      value: overdueIssues,
      icon: <ScheduleIcon />,
      color: '#d32f2f',
      subtitle: 'Past due date',
    },
    {
      title: 'Weekly Trend',
      value: `${trendPercentage > 0 ? '+' : ''}${trendPercentage}%`,
      icon: <TrendingUpIcon />,
      color: trendPercentage > 0 ? '#d32f2f' : '#2e7d32',
      subtitle: 'vs last week',
    },
    {
      title: 'Avg Resolution',
      value: avgResolutionTime,
      icon: <ScheduleIcon />,
      color: '#1976d2',
      subtitle: 'Time to resolve',
    },
  ]

  const DashboardCard = ({ title, value, icon, color, subtitle }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h3" component="div" sx={{ color }}>
              {value}
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {subtitle}
            </Typography>
          </Box>
          <Box sx={{ color, opacity: 0.6 }}>
            {React.cloneElement(icon, { sx: { fontSize: 40 } })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  )

  return (
    <Box sx={{ mb: 3 }}>
      
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {dashboardCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
            <DashboardCard {...card} />
          </Grid>
        ))}
      </Grid>

      {/* Progress Indicators */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <TrendingUpIcon sx={{ mr: 1, color: '#1976d2' }} />
                <Typography variant="h6">Completion Rate</Typography>
              </Box>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography variant="h4" sx={{ mr: 1 }}>
                  {completionRate.toFixed(1)}%
                </Typography>
                <Typography color="textSecondary" variant="body2">
                  of all issues resolved
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={completionRate}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                  },
                }}
              />
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                {resolvedIssues + closedIssues} of {totalIssues} issues
                completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <ScheduleIcon sx={{ mr: 1, color: '#2e7d32' }} />
                <Typography variant="h6">Performance Metrics</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">
                  Average Resolution Time
                </Typography>
                <Typography variant="h4">{avgResolutionTime}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Issues This Month
                </Typography>
                <Typography variant="h5">
                  {
                    issues.filter((issue) => {
                      const issueDate = new Date(issue.createdAt)
                      const now = new Date()
                      return (
                        issueDate.getMonth() === now.getMonth() &&
                        issueDate.getFullYear() === now.getFullYear()
                      )
                    }).length
                  }
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Breakdown Charts */}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Issues by Category
              </Typography>
              {Object.entries(categoryBreakdown).length > 0 ? (
                Object.entries(categoryBreakdown)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 5)
                  .map(([category, count]) => (
                    <Box key={category} sx={{ mb: 1 }}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="body2">{category}</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {count}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(count / totalIssues) * 100}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  ))
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No issues to display
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Issues by Plant
              </Typography>
              {Object.entries(plantBreakdown).length > 0 ? (
                Object.entries(plantBreakdown)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 5)
                  .map(([plant, count]) => (
                    <Box key={plant} sx={{ mb: 1 }}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ maxWidth: '70%' }}
                        >
                          {plant}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {count}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(count / totalIssues) * 100}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  ))
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No issues to display
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default IssueDashboard
